import type { ButtonProps } from './buttonTypes';

import { isMobile } from '/shared/functions/isMobile';

import getIcon from './__functions__/getIcon';

import ButtonIcon from './ButtonIcon';
import ButtonChildren from './ButtonChildren';

import StyledButton from './__styled__/StyledButton';
import { StyledButtonLoading, StyledButtonLoadingSpinner } from './__styled__/StyledButtonLoading';

function Button(props: ButtonProps) {
  const {
    children = null,
    type = null,
    $primary = false,
    $secondary = false,
    $outline = false,
    $active = false,
    testId = null,
    $icon = null,
    $iconAfter = null,
    isMobileOnly = false,
    $small = false,
    $jumbo,
    $hiddenText,
    $isLoading = false,
    color,
    disabled = false,
    ...rest
  } = props;

  const assumedType = type ?? ($primary ? 'submit' : 'button');
  if (!children && !$icon) {
    throw new Error('A button must either have children or an icon');
  }

  let buttonOutlineReplacement = false;
  if ($outline && $active && !$jumbo && !$small && !$secondary) {
    buttonOutlineReplacement = true;
  }

  if (!isMobile && isMobileOnly) {
    return null;
  }

  return (
    <StyledButton
      data-test-id={testId}
      $hasChildren={Boolean(children)}
      $hasIcon={Boolean($icon)}
      $hasIconAfter={Boolean($iconAfter)}
      type={assumedType}
      $primary={$primary}
      $secondary={$secondary}
      $outline={$outline}
      $outlineReplacement={buttonOutlineReplacement}
      $small={$small}
      $jumbo={$jumbo}
      $active={$active}
      $hiddenText={$hiddenText}
      $isLoading={$isLoading}
      color={color}
      disabled={disabled || $isLoading}
      aria-busy={$isLoading || undefined}
      {...(rest as Omit<typeof rest, 'style'> & {
        style?: React.CSSProperties & Record<`--${string}`, string | number>;
      })}
    >
      <div>
        {$icon && <ButtonIcon {...props} displayIcon={getIcon($icon, props)} />}
        {children && <ButtonChildren {...props}>{children}</ButtonChildren>}
        {$iconAfter && <ButtonIcon {...props} displayIcon={getIcon($iconAfter, props)} />}
      </div>
      <StyledButtonLoading aria-hidden="true" $isLoading={$isLoading}>
        <StyledButtonLoadingSpinner />
      </StyledButtonLoading>
    </StyledButton>
  );
}

export default Button;
